import { faCalendar, faMoon, faPlane, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';
import Slider from 'react-slick';
import { useTranslation } from 'react-i18next';
import { getImgUrl } from '../../utils';
import { cdnUrl } from '../../constants';
import { dateFormat } from '../../service';

const normalizeDateToIso = (value?: string) => {
  if (!value) return '';

  const trimmed = value.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) return trimmed;

  const dashMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(trimmed);
  if (dashMatch) return `${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`;

  const dotMatch = /^(\d{2})\.(\d{2})\.(\d{2}|\d{4})$/.exec(trimmed);
  if (dotMatch) {
    const year = dotMatch[3].length === 2 ? `20${dotMatch[3]}` : dotMatch[3];
    return `${year}-${dotMatch[2]}-${dotMatch[1]}`;
  }

  return trimmed;
};

const DetailsSkeleton = () => {
  const [t] = useTranslation() as unknown as [(key: string) => string];
  const [urlParams] = useSearchParams();
  const searchKey = 'search' + urlParams.get('dealId') + (urlParams.get('city') || urlParams.get('cityCode')) + urlParams.get('checkIn');
  const search = sessionStorage.getItem(searchKey);
  const searchObj = search ? JSON.parse(search) : null;
  const photos = searchObj?.allPhotos || [];
  const checkInIso = normalizeDateToIso(searchObj?.checkIn);
  const checkOutIso = normalizeDateToIso(searchObj?.checkOut);

  return (
    <div className="oph-details-wrapper">
      <div className="oph-details-first mb-20">
        <div className="default-shadow oph-details-center">
          <div className="text-bold text-lg">
            <span className="name">
              {searchObj?.hotelTitle || searchObj?.hotelName || <Skeleton height={33} width={'50%'} />}
            </span>
          </div>
          <div className="position-relative">
            {photos.length ? (
              <Slider arrows={true} dots className="oph-details-slider">
                {photos.map((image: any, index: number) => (
                  <img key={index} src={getImgUrl(image.link, cdnUrl)} alt={image.alt} />
                ))}
              </Slider>
            ) : (
              <Skeleton height={'calc(100% - 50px)'} width={'100%'} />
            )}
          </div>
        </div>
        <div className="default-shadow oph-details-sidebar">
          <div className="title text-md">
            <span className="name">
              {searchObj?.hotelTitle || searchObj?.hotelName || <Skeleton height={23} width={150} />}
            </span>
          </div>
          <div className="mb-10 display-flex" style={{ textTransform: 'capitalize' }}>
            <FontAwesomeIcon icon={faCalendar as any} />
            {checkInIso ? `${t('tour.entrance')}: ${dateFormat(checkInIso, 'EEEE - d MMMM, yyyy', 'ru')}` : <Skeleton height={24} width={'80%'} />}
          </div>
          <div className="mb-10 display-flex" style={{ textTransform: 'capitalize' }}>
            <FontAwesomeIcon icon={faCalendar as any} />
            {checkOutIso ? `${t('tour.exit')}: ${dateFormat(checkOutIso, 'EEEE - d MMMM, yyyy', 'ru')}` : <Skeleton height={24} width={'80%'} />}
          </div>
          <div className="mb-10 display-flex">
            <FontAwesomeIcon icon={faPlane as any} />
            {<Skeleton height={24} width={150} style={{ display: 'inline-block' }} />}
          </div>
          <div className="mb-10 display-flex">
            <FontAwesomeIcon icon={faMoon as any} />
            {searchObj?.nightsCount || searchObj?.nights ? `${t('tour.nights')}: ${searchObj.nightsCount || searchObj.nights}` : <Skeleton height={24} width={'70%'} />}
          </div>
          <div className="mb-10 display-flex">
            <FontAwesomeIcon icon={faUser as any} />
            {<Skeleton height={24} width={120} />}
          </div>
        </div>
      </div>

      <div className="oph-details-title mb-10">
        <Skeleton height={30} width={200} />
      </div>
      <TicketSkeleton />
      <TicketSkeleton />
    </div>
  );
};

const TicketSkeleton = () => {
  return (
    <div className="default-shadow mb-20">
      <div className="oph-ticket">
        <div className="oph-ticket-header">
          <span>
            <Skeleton height={24} width={100} />
          </span>
        </div>
        <div className="oph-ticket-logo">
          <Skeleton height={70} width={90} />
          <Skeleton height={19.5} width={40} />
        </div>
        <div className="oph-ticket-route">
          <div className="city">
            <Skeleton height={22.5} width={80} />
          </div>
          <div className="time">
            <Skeleton height={30} width={50} />
          </div>
          <div className="date">
            <Skeleton height={19.5} width={100} />
          </div>
        </div>
        <div className="oph-ticket-separator">
          <span className="type-flight">
            <Skeleton height={21} width={100} />
          </span>
          <span className="circle"></span>
          <FontAwesomeIcon icon={faPlane as any} />
        </div>
        <div className="oph-ticket-route">
          <div className="city">
            <Skeleton height={22.5} width={80} />
          </div>
          <div className="time">
            <Skeleton height={30} width={50} />
          </div>
          <div className="date">
            <Skeleton height={19.5} width={100} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsSkeleton;
